import React from 'react';
import { useTranslation } from 'react-i18next';
import GetAppIcon from '@material-ui/icons/GetApp';

import useOffersApprovals from 'data/useOffersApprovals';
import { getTableColumns } from 'utils/tablesUtils';
import TableContainer from 'components/TableContainer/TableContainer';
import FullWidthTableLayout from 'layouts/FullWidthTableLayout';
import { PrimaryButtonLoader } from 'components/Buttons/Buttons';
import useDialog from 'hooks/useDialog';
import downloadExcel from 'utils/downloadExcel';
import ResponseMessageDialog from 'components/ResponseMessageDialog/ResponseMessageDialog';
import { getApprovalsOffersExcelFile } from 'api/approvals';

import styles from '../../styles/components/pagesWithTable.module.scss';

const ApprovalsOffers = ({ children }) => {
  const { t, ready } = useTranslation([
    'offersApprovals',
    'translation',
    'datagrid',
  ]);
  const translation = ready
    ? {
        pageTitle: t(`offersApprovals:pageTitle`),
        titles: t(`offersApprovals:titles`, {
          returnObjects: true,
        }),
        sorting: t(`datagrid:sortingDisclaimer`, { stars: '*' }),
        errorButton: t(`translation:retry`),
        downloadButtonLabel: t(`offersApprovals:downloadButtonLabel`),
      }
    : '';

  const { rows, loading, error } = useOffersApprovals();
  const columns = ready
    ? getTableColumns('offersApprovals', translation.titles)
    : [];

  const {
    isOpen,
    isLoading,
    openDialog,
    closeDialog,
    startLoading,
    stopLoading,
    handleError,
    dialogErrorMessage,
  } = useDialog(false);

  const requestExcel = async () => {
    startLoading();
    try {
      const response = await getApprovalsOffersExcelFile();
      downloadExcel(response.data, `Offers Approvals List`);
      stopLoading();
    } catch (error) {
      openDialog();
      try {
        handleError(
          JSON.parse(await error.response?.data?.text())?.error?.userMessage
        );
      } catch (error) {
        handleError(translation?.somethingWrong);
      }
    }
  };

  return (
    <FullWidthTableLayout ready={columns.length > 0}>
      <TableContainer
        tableData={{
          columns,
          rows,
        }}
        loading={loading}
        error={error}
        disclaimerElements={[
          <span key={1}>{translation?.sorting}</span>,
          <span key={2}>{translation?.dates}</span>,
        ]}
        pageName="ApprovalsOffers"
      >
        <h1 className={styles.title}>{translation?.pageTitle}</h1>
        <div className={styles.buttonsContainer}>
          <PrimaryButtonLoader
            className={styles.button}
            aria-label={translation?.downloadButtonLabel}
            title={translation?.downloadButtonLabel}
            onClick={requestExcel}
            loading={isLoading}
          >
            <GetAppIcon />
          </PrimaryButtonLoader>
        </div>
      </TableContainer>
      {children}
      <ResponseMessageDialog
        open={isOpen}
        dialogActions={
          <PrimaryButtonLoader onClick={requestExcel}>
            {translation?.errorButton}
          </PrimaryButtonLoader>
        }
        closeDialog={closeDialog}
        dialogHeader={translation?.downloadButtonLabel}
        isLoading={isLoading}
        success={false}
      >
        {dialogErrorMessage}
      </ResponseMessageDialog>
    </FullWidthTableLayout>
  );
};

export default ApprovalsOffers;
